import React, { useState, useEffect, useCallback } from "react"
import { authFetch, buildQuery } from "../../lib/api"

export default function AdminUsers() {
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [search, setSearch] = useState("")
  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [activityUserId, setActivityUserId] = useState(null)
  const [activity, setActivity] = useState(null)
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" })
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "user" })
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setError("")
    const q = buildQuery({ page: 1, limit: 100, search: search || undefined })
    authFetch(`/api/admin/users${q}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error)
        else setList(data.users || [])
      })
      .catch(() => setError("Failed to load users"))
      .finally(() => setLoading(false))
  }, [search])

  useEffect(() => {
    load()
  }, [load])

  const handleCreateSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError("")
    try {
      const res = await authFetch("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          role: form.role,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || "Create failed")
        return
      }
      setShowCreate(false)
      setForm({ name: "", email: "", password: "", role: "user" })
      load()
    } catch (err) {
      setError("Request failed")
    } finally {
      setSaving(false)
    }
  }

  const openEdit = (selectedUser) => {
    setEditingId(selectedUser.id)
    setEditForm({ name: selectedUser.name || "", email: selectedUser.email || "", role: selectedUser.role || "user" })
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    if (!editingId) return
    setSaving(true)
    setError("")
    try {
      const res = await authFetch(`/api/admin/users/${editingId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || "Update failed")
        return
      }
      setEditingId(null)
      load()
    } catch (err) {
      setError("Request failed")
    } finally {
      setSaving(false)
    }
  }

  const loadActivity = (userId) => {
    setActivityUserId(userId)
    setActivity(null)
    authFetch(`/api/admin/users/${userId}/activity`)
      .then((r) => r.json())
      .then((data) => setActivity(data))
      .catch(() => setActivity({ error: "Failed to load" }))
  }

  return (
    <div className='dashboard-stack'>
      <section className='dashboard-card'>
        <div className='dashboard-card__header'>
          <div>
            <span className='dashboard-eyebrow'>Accounts</span>
            <h2>Users</h2>
            <p>Create, edit, search, and inspect user activity.</p>
          </div>
          <button type='button' className='dashboard-primary-action admin-primary-action' onClick={() => setShowCreate(true)}>
            <i className='fa-solid fa-user-plus'></i>
            Create user
          </button>
        </div>

        {error && <p className='dashboard-error'>{error}</p>}

        <div className='admin-toolbar'>
          <input
            type='text'
            placeholder='Search by email or name'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {showCreate && (
          <form onSubmit={handleCreateSubmit} className='admin-form admin-form--wide'>
            <h3>New user</h3>
            <label>
              Name *
              <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </label>
            <label>
              Email *
              <input type='email' required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </label>
            <label>
              Password * (min 6)
              <input type='password' required minLength={6} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
            </label>
            <label>
              Role
              <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                <option value='user'>user</option>
                <option value='admin'>admin</option>
              </select>
            </label>
            <div className='admin-form__actions'>
              <button type='submit' className='dashboard-primary-action admin-primary-action' disabled={saving}>
                {saving ? "Saving..." : "Create"}
              </button>
              <button type='button' className='btn btn--secondary' onClick={() => setShowCreate(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : list.length === 0 ? (
          <div className='dashboard-empty'>No users found.</div>
        ) : (
          <div className='admin-table-wrap'>
            <table className='admin-table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((listedUser) => (
                  <tr key={listedUser.id}>
                    <td>
                      {editingId === listedUser.id ? (
                        <input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
                      ) : (
                        <strong>{listedUser.name}</strong>
                      )}
                    </td>
                    <td>
                      {editingId === listedUser.id ? (
                        <input type='email' value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
                      ) : (
                        <span className='admin-table__muted'>{listedUser.email}</span>
                      )}
                    </td>
                    <td>
                      {editingId === listedUser.id ? (
                        <select value={editForm.role} onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}>
                          <option value='user'>user</option>
                          <option value='admin'>admin</option>
                        </select>
                      ) : (
                        <span className={`admin-role-pill ${listedUser.role === "admin" ? "admin-role-pill--admin" : ""}`}>
                          {listedUser.role}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className='dashboard-list__actions'>
                        {editingId === listedUser.id ? (
                          <>
                            <button type='button' className='btn btn--secondary' onClick={handleEditSubmit} disabled={saving}>
                              Save
                            </button>
                            <button type='button' className='btn btn--danger' onClick={() => setEditingId(null)}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button type='button' className='btn btn--secondary' onClick={() => openEdit(listedUser)}>
                              Edit
                            </button>
                            <button type='button' className='btn btn--secondary' onClick={() => loadActivity(listedUser.id)}>
                              Activity
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {activityUserId && (
        <section className='dashboard-card'>
          <div className='dashboard-card__header'>
            <div>
              <span className='dashboard-eyebrow'>User activity</span>
              <h2>Account activity</h2>
            </div>
            <button type='button' className='btn btn--secondary' onClick={() => setActivityUserId(null)}>
              Close
            </button>
          </div>
          {activity && (
            <>
              {activity.error && <p className='dashboard-error'>{activity.error}</p>}
              {!activity.error && (
                <div className='admin-activity-grid'>
                  <div><strong>{activity.savedSearches?.length || 0}</strong><span>Saved searches</span></div>
                  <div><strong>{activity.savedHomes?.length || 0}</strong><span>Saved homes</span></div>
                  <div><strong>{activity.rentalApplications?.length || 0}</strong><span>Applications</span></div>
                  <div><strong>{activity.bookings?.length || 0}</strong><span>Bookings</span></div>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  )
}
